import { once } from 'lodash';

import { FlagType } from '~/flags/flags';
import { getFlag } from '~/flags/useFlag';
import { JsonApiGetResponse } from '~/redux/api/ApiTypes';
import { pilotV3 } from '~/services/serviceInstances';
import { StorageService } from '~/services/StorageService/StorageService';
import { RbacPermission } from '~/types/Rbac';

type CheckPermissionResult = JsonApiGetResponse<
  'permission_check',
  {
    permission: boolean;
  }
>;

const baseCheckPermission = async ({
  resourceId,
  resourceType,
}: RbacPermission) => {
  const useLocalStorageCache = getFlag(FlagType.UseLocalStorageCache);
  const key = `permission-${resourceId}-${resourceType}`;
  const cachedValue =
    await StorageService.getSecureItem<CheckPermissionResult>(key);

  const responsePromise = pilotV3.get<CheckPermissionResult>('/permission/', {
    params: {
      resource_id: resourceId,
      resource_type: resourceType,
    },
  });

  if (useLocalStorageCache) {
    responsePromise.then((response) => {
      StorageService.setSecureItem(key, response);
    });

    if (cachedValue) {
      return cachedValue;
    }
  }

  return responsePromise;
};

const protectedFns: Record<string, typeof baseCheckPermission> = {};

export const checkPermission = async ({
  resourceId,
  resourceType,
}: RbacPermission) => {
  const key = `permission-${resourceId}-${resourceType}`;

  if (!protectedFns[key]) {
    protectedFns[key] = once(baseCheckPermission);
  }

  return protectedFns[key]({ resourceId, resourceType });
};
